// useState that lives in the page's URL (?key=value), for what should come
// back with the Back button — the open list, the selected tab, the period —
// but not stay forever the way useRememberedState's filters do (client,
// 2026-10-30: "wapis aane pe pura wapis se kholna padta hai"). Changes replace
// the current history entry, so Back still goes to the previous screen.
import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const encode = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v));

function decode<T>(raw: string | null, initial: T): T {
  if (raw == null) return initial;
  if (typeof initial === 'string') return raw as T;
  try { return JSON.parse(raw) as T; } catch { return initial; }
}

export function useUrlState<T>(key: string, initial: T): [T, (next: T | ((cur: T) => T)) => void] {
  const [params, setParams] = useSearchParams();
  // `initial` is a constant default per call site; held so the setter stays stable.
  const initialRef = useRef(initial);
  const raw = params.get(key);
  const value = useMemo(() => decode(raw, initialRef.current), [raw]);

  const set = useCallback((next: T | ((cur: T) => T)) => {
    setParams((cur) => {
      const prev = decode(cur.get(key), initialRef.current);
      const v = typeof next === 'function' ? (next as (c: T) => T)(prev) : next;
      const out = new URLSearchParams(cur);
      if (v == null || encode(v) === encode(initialRef.current)) out.delete(key);
      else out.set(key, encode(v));
      return out;
    }, { replace: true });
  }, [key, setParams]);

  return [value, set];
}
